// Global cache for loaded models and data
// This ensures objects are only loaded once and reused from cache

class ModelCache {
  constructor(maxEntries = 48) {
    this.maxEntries = maxEntries;
    this.cache = new Map();
    this.loadingPromises = new Map();
  }

  evict() {
    while (this.cache.size > this.maxEntries) {
      const oldest = this.cache.keys().next().value;
      this.cache.delete(oldest);
    }
  }

  // Get cached model or return null if not cached
  get(key) {
    if (!this.cache.has(key)) return null;
    const value = this.cache.get(key);
    this.cache.delete(key);
    this.cache.set(key, value);
    return value;
  }

  // Check if model is cached
  has(key) {
    return this.cache.has(key);
  }

  // Check if model is currently loading
  isLoading(key) {
    return this.loadingPromises.has(key);
  }

  // Get or wait for loading promise
  getLoadingPromise(key) {
    return this.loadingPromises.get(key) || null;
  }

  // Set cached model
  set(key, value) {
    if (this.cache.has(key)) this.cache.delete(key);
    this.cache.set(key, value);
    this.evict();
    return value;
  }

  // Set loading promise
  setLoadingPromise(key, promise) {
    this.loadingPromises.set(key, promise);
    promise
      .then(() => {
        // Keep promise resolved, cache will be set separately
      })
      .catch(() => {
        // Remove promise on error so it can be retried
        this.loadingPromises.delete(key);
      });
    return promise;
  }

  // Remove loading promise (called after successful load)
  removeLoadingPromise(key) {
    this.loadingPromises.delete(key);
  }

  // Clear cache for a specific key
  delete(key) {
    this.cache.delete(key);
    this.loadingPromises.delete(key);
  }

  /** Drop RAM entries whose key contains needle (path without query is enough). */
  deleteMatching(substr) {
    const needle = String(substr || '').split('?')[0];
    if (!needle) return;
    for (const key of [...this.cache.keys()]) {
      if (key.includes(needle)) this.cache.delete(key);
    }
    for (const key of [...this.loadingPromises.keys()]) {
      if (key.includes(needle)) this.loadingPromises.delete(key);
    }
  }

  // Clear all cache
  clear() {
    this.cache.clear();
    this.loadingPromises.clear();
  }

  // Get cache size
  size() {
    return this.cache.size;
  }
}

// Export singleton instance
export const modelCache = new ModelCache();
